import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/** Shared with PHP `website/script.js` — do not rename without updating both. */
export const CONSENT_KEY = 'nkcCookieConsent';
export const CONSENT_COOKIE = 'nkc_cookie_consent';
/** Preference retention: ~30 minutes (revised cookie policy). */
export const CONSENT_TTL_MS = 30 * 60 * 1000;

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function writeConsentCookie(value, maxAgeSeconds) {
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(value)};path=/;max-age=${maxAgeSeconds};SameSite=Lax${secure}`;
}

function clearConsentCookie() {
  document.cookie = `${CONSENT_COOKIE}=;path=/;max-age=0;SameSite=Lax`;
}

export function readConsentRecord() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY) || readCookie(CONSENT_COOKIE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.accepted !== true || typeof data.acceptedAt !== 'number') return null;
    if (Date.now() - data.acceptedAt > CONSENT_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

export function hasValidConsent() {
  return readConsentRecord() !== null;
}

export function saveConsent() {
  const record = {
    accepted: true,
    acceptedAt: Date.now(),
    expiresInMinutes: 30,
  };
  const raw = JSON.stringify(record);
  try {
    localStorage.setItem(CONSENT_KEY, raw);
  } catch {
    /* ignore quota / private mode */
  }
  try {
    writeConsentCookie(raw, Math.floor(CONSENT_TTL_MS / 1000));
  } catch {
    /* ignore */
  }
}

export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
  try {
    clearConsentCookie();
  } catch {
    /* ignore */
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (hasValidConsent()) {
      // Refresh cookie expiry window if only localStorage survived
      const record = readConsentRecord();
      if (record) {
        try {
          writeConsentCookie(JSON.stringify(record), Math.floor(CONSENT_TTL_MS / 1000));
        } catch {
          /* ignore */
        }
      }
      return undefined;
    }
    setMounted(true);
    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
      document.body.classList.add('is-cookie-consent-open');
    });
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.classList.remove('is-cookie-consent-open');
    };
  }, []);

  useEffect(() => {
    function onResetClick(e) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('#cookieSettingsReset')) return;
      e.preventDefault();
      clearConsent();
      window.location.reload();
    }
    document.addEventListener('click', onResetClick);
    return () => document.removeEventListener('click', onResetClick);
  }, []);

  function dismiss() {
    saveConsent();
    setVisible(false);
    document.body.classList.remove('is-cookie-consent-open');
    window.setTimeout(() => setMounted(false), 360);
  }

  if (!mounted) return null;

  return (
    <div
      id="cookieConsent"
      className={`cookie-consent${visible ? ' is-visible' : ''}`}
      role="dialog"
      aria-labelledby="cookieConsentTitle"
      aria-describedby="cookieConsentDesc"
      aria-live="polite"
    >
      <div className="cookie-consent__inner container">
        <div className="cookie-consent__content">
          <p id="cookieConsentTitle" className="cookie-consent__title">
            Cookies on this website
          </p>
          <p id="cookieConsentDesc" className="cookie-consent__text">
            We use cookies and similar technologies so this website can work, remember your
            preferences, and (where enabled) measure usage and deliver relevant content. Your
            choice is remembered for approximately <strong>30 minutes</strong>. Read our{' '}
            <Link to="/cookies">cookie policy</Link> for details.
          </p>
        </div>
        <div className="cookie-consent__actions">
          <Link to="/cookies" className="cookie-consent__link">
            Cookie settings
          </Link>
          <button type="button" className="cookie-consent__accept" onClick={dismiss}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
